import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Calendar } from "../ui/calendar";

// Mobile bottom day picker — the same strip as the NHL schedule (full month of day chips + pull-up calendar).
export function DayStrip({ curDay, todayIso, onPick, gamesOnDay = {}, prefix, accent = "#9D4CDD", unit = "Game" }) {
    const [calOpen, setCalOpen] = useState(false);
    const centre = useCallback((st) => {
        const el = st && st.querySelector('[data-on="1"]');
        if (el) st.scrollLeft = el.offsetLeft - st.clientWidth / 2 + el.clientWidth / 2;
    }, [curDay]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!curDay) return null;
    const [yy, mm] = curDay.split("-").map(Number);
    const nDays = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
    const prevM = new Date(Date.UTC(yy, mm - 1, 0)).toISOString().slice(0, 10);
    const nextM = new Date(Date.UTC(yy, mm, 1)).toISOString().slice(0, 10);
    const mLbl = (x) => new Date(x + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    const monthChip = (to, dir) => (
        <button type="button" onClick={() => onPick(to)} data-testid={`${prefix}-month-${dir}`}
            className="snap-center shrink-0 w-12 h-14 rounded-lg border border-dashed border-white/20 text-white/70 flex flex-col items-center justify-center">
            {dir === "prev" ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="text-[8px] font-black uppercase">{mLbl(to)}</span>
        </button>
    );
    return createPortal(
        <div className="fixed vv-anchored left-0 right-0 bottom-[calc(48px+env(safe-area-inset-bottom))] z-[53] px-3 pt-2 pb-2 bg-[#0A0B10]/95 backdrop-blur-md border-t border-white/10" data-testid={`${prefix}-daystrip`}>
            <button type="button" onClick={() => setCalOpen((v) => !v)} data-testid={`${prefix}-calendar-pull`} aria-label="Pick a date"
                className="w-full h-4 -mt-1 mb-1 flex items-center justify-center text-white/40 active:text-white">
                <span className="h-px flex-1 bg-white/10" />
                {calOpen ? <ChevronDown className="h-3.5 w-3.5 mx-2" /> : <ChevronUp className="h-3.5 w-3.5 mx-2" />}
                <span className="h-px flex-1 bg-white/10" />
            </button>
            {calOpen && (
                <div className="flex justify-center mb-2" data-testid={`${prefix}-calendar-panel`}>
                    <Calendar mode="single" selected={new Date(curDay + "T12:00:00")} defaultMonth={new Date(curDay + "T12:00:00")}
                        onSelect={(d) => { if (d) { onPick(d.toLocaleDateString("en-CA")); setCalOpen(false); } }} />
                </div>
            )}
            <div ref={centre} className="flex gap-1.5 overflow-x-auto snap-x pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ WebkitOverflowScrolling: "touch" }} data-testid={`${prefix}-monthbar`}>
                {monthChip(prevM, "prev")}
                {Array.from({ length: nDays }, (_, i) => `${curDay.slice(0, 8)}${String(i + 1).padStart(2, "0")}`).map((iso) => {
                    const on = iso === curDay;
                    const d = new Date(iso + "T12:00:00Z");
                    const n = gamesOnDay[iso] || 0;
                    return (
                        <button key={iso} type="button" onClick={() => onPick(iso)} data-testid={`${prefix}-daychip-${iso}`} data-on={on ? "1" : undefined}
                            className={`snap-center shrink-0 w-12 h-[66px] rounded-lg border flex flex-col items-center justify-center ${on ? "border-white/60 bg-white/15 text-white" : iso === todayIso ? "text-white" : "border-white/10 text-white/55"}`}
                            style={!on && iso === todayIso ? { borderColor: `${accent}B3` } : undefined}>
                            <span className="text-[9px] font-black uppercase">{d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}</span>
                            <span className="text-base font-black leading-none">{d.getUTCDate()}</span>
                            <span className="text-[8px] font-bold uppercase text-white/40">{d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}</span>
                            <span className={`mt-0.5 text-[7px] font-bold uppercase tracking-tight leading-none whitespace-nowrap ${n ? "text-white/60" : "text-white/20"}`}>{n} {unit === "Stor" ? (n === 1 ? "Story" : "Stories") : `${unit}${n === 1 ? "" : "s"}`}</span>
                        </button>
                    );
                })}
                {monthChip(nextM, "next")}
            </div>
        </div>,
        document.body
    );
}
